import { pWarn } from '_/helpers/logger';
import { eventBus } from '_/helpers/eventBus';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet's icon paths
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const photonConfig = require('~/config/config.json');

export default {
    props: {
        vuexModule: {
            required: true,
            type: String,
        },
        defaultValue: {
            type: [Boolean, String],
        },
        disabled: {
            type: Boolean,
        },
        id: {
            required: true,
            type: [Number, String],
        },
        name: {
            required: true,
            type: String,
        },
        type: {
            default: 'text',
            type: String,
        },
        value: {
            default: null,
            type: [Number, String],
        },
        focus: {
            default: false,
            type: Boolean,
        },
        refreshFields: {
            type: Number,
        },
    },

    data() {
        return {
            map: null,
            marker: null,
            defaultZoom: 13,
            markerZoom: 16,
            currentValue: null,
            lastMapState: null,
            isTransitioning: false
        };
    },

    computed: {
        center() {
            return this.marker ? this.marker : photonConfig.locationStartCoordinates;
        }
    },

    watch: {
        value: {
            immediate: true,
            handler(newValue, oldValue) {
                if (newValue === null && oldValue && !this.isTransitioning) {
                    this.saveMapState();
                }

                this.currentValue = newValue;
                if (this.map) {
                    if (newValue) {
                        this.isTransitioning = true;
                        this.$nextTick(() => {
                            this.setMarkerLatLng();
                            this.$nextTick(() => {
                                this.isTransitioning = false;
                            });
                        });
                    } else if (!this.isTransitioning) {
                        this.clearMarker();
                    }
                }
            }
        },

        refreshFields: {
            handler(newValue, oldValue) {
                if (newValue !== oldValue) {
                    if (this.lastMapState && !this.value) {
                        this.restoreMapState();
                    }
                    
                    this.setDefaultValueIfRequired();
                    if (this.map && this.value) {
                        this.setMarkerLatLng();
                    }
                }
            }
        }
    },

    methods: {
        createEventPayload(event) {
            return {
                value: event.target.value,
                event,
            };
        },

        onChange(event) {
            const payload = this.createEventPayload(event);
            this.$emit('change', payload);
        },

        onBlur(event) {
            const payload = this.createEventPayload(event);
            eventBus.$emit('global-change', payload);
        },

        setDefaultValueIfRequired() {
            if (this.defaultValue && !this.value) {
                this.$emit('input', this.defaultValue);
            }
        },

        clearMarker() {
            if (this.marker && !this.isTransitioning) {
                this.map.removeLayer(this.marker);
                this.marker = null;
                this.$emit('input', '');
            }
        },

        saveMapState() {
            if (this.map) {
                const center = this.map.getCenter();
                this.lastMapState = {
                    center: { lat: center.lat, lng: center.lng },
                    zoom: this.map.getZoom()
                };
            }
        },

        restoreMapState() {
            if (this.map && this.lastMapState) {
                this.map.setView(
                    [this.lastMapState.center.lat, this.lastMapState.center.lng],
                    this.lastMapState.zoom
                );
            }
        },

        getCoordinatesFromValue() {
            try {
                const coords = this.value.split(/,\s*/);
                if (coords.length === 2) {
                    const [lat, lng] = coords.map(Number);
                    if (!isNaN(lat) && !isNaN(lng)) {
                        return { lat, lng };
                    }
                }
            } catch (error) {
                pWarn('Invalid location format');
            }
            return this.center;
        },

        setMarker(e) {
            const latlng = e.latlng || e;
            
            if (this.marker) {
                this.marker.setLatLng(latlng);
            } else {
                this.marker = L.marker(latlng).addTo(this.map);
            }

            const lat = latlng.lat.toFixed(6);
            const lng = latlng.lng.toFixed(6);
            const value = `${lat}, ${lng}`;
            
            this.$emit('input', value);
            this.$emit('change', {
                value: value,
                event: {
                    target: {
                        value: value,
                        id: this.id,
                        name: this.name
                    }
                }
            });
        },

        setMarkerLatLng() {
            if (!this.map) return;

            try {
                const coords = this.value.split(/,\s*/);
                if (coords.length === 2) {
                    const [lat, lng] = coords.map(Number);
                    if (!isNaN(lat) && !isNaN(lng)) {
                        if (this.marker) {
                            this.map.removeLayer(this.marker);
                            this.marker = null;
                        }
                        const latLng = [lat, lng];
                        this.marker = L.marker(latLng).addTo(this.map);
                        this.map.setView(latLng, this.markerZoom);
                    }
                }
            } catch (error) {
                pWarn('Invalid location format');
            }
        },

        initMap() {
            const initialState = this.lastMapState || {
                center: this.value ? this.getCoordinatesFromValue() : {
                    lat: this.center.lat || 51.505,
                    lng: this.center.lng || -0.09
                },
                zoom: this.value ? this.markerZoom : this.defaultZoom
            };

            if (this.map) {
                this.map.remove();
            }

            this.map = L.map('map').setView([
                initialState.center.lat,
                initialState.center.lng
            ], initialState.zoom);

            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: ' OpenStreetMap contributors'
            }).addTo(this.map);

            this.map.on('click', this.setMarker);

            if (this.value) {
                this.$nextTick(() => {
                    this.setMarkerLatLng();
                });
            }
        }
    },

    mounted() {
        this.$nextTick(() => {
            this.initMap();
            this.setDefaultValueIfRequired();
        });
    },

    beforeDestroy() {
        if (this.map) {
            this.map.remove();
        }
    }
};
